// @flow

import _ from 'lodash';

import { pluralize } from '@zapier/common-utils';

import { CREDIT_CARD_URL } from 'app/settings/utils/constants';

export const STATUS_ICONS = {
  success: 'check-alt',
  error: 'stopped',
  halted: 'stopped',
  filtered: 'filter',
  delayed: 'clock',
  scheduled: 'clock',
  pending: 'progress',
  deleted: 'delete',

  // did not run reasons:
  paused_zap: 'stopped',
  successful_task: 'stopped',
  outstanding_task: 'clock',
  diverged_zap: 'stopped',
  expired_task: 'stopped',
  unsuccessful_task: 'stopped',
  deleted_zap: 'stopped',
  held: 'holding',
  playing_task: 'playing',
  throttled: 'holding',

  must_upgrade: 'delete',
};

const DEFAULT_REPLAY_LEARN_MORE_URL =
  '/help/manage/tasks/replay-failed-tasks-in-zaps';

export const REPLAY_LEARN_MORE_URLS = {
  // successful
  success: DEFAULT_REPLAY_LEARN_MORE_URL,

  error: DEFAULT_REPLAY_LEARN_MORE_URL,
  halted: DEFAULT_REPLAY_LEARN_MORE_URL,
  filtered: DEFAULT_REPLAY_LEARN_MORE_URL,
  delayed: DEFAULT_REPLAY_LEARN_MORE_URL,
  scheduled: DEFAULT_REPLAY_LEARN_MORE_URL,
  pending: DEFAULT_REPLAY_LEARN_MORE_URL,

  // did not run reasons:
  paused_zap: DEFAULT_REPLAY_LEARN_MORE_URL,
  successful_task: DEFAULT_REPLAY_LEARN_MORE_URL,
  outstanding_task: DEFAULT_REPLAY_LEARN_MORE_URL,
  diverged_zap: DEFAULT_REPLAY_LEARN_MORE_URL,
  expired_task: DEFAULT_REPLAY_LEARN_MORE_URL,
  unsuccessful_task: DEFAULT_REPLAY_LEARN_MORE_URL,
  deleted_zap: DEFAULT_REPLAY_LEARN_MORE_URL,
  playing_task: DEFAULT_REPLAY_LEARN_MORE_URL,

  must_upgrade: null, // no 'learn more' for this one
};

export const SUB_STATUS_ICONS = {
  error: 'bolt',
  halted: 'cloud',
  filtered: 'filter',
  delayed: 'clock',
  scheduled: 'calendar',
  throttled: 'throttle',
};

export const BLACKLISTED_APP = 'access_blacklisted_app';
export const PREMIUM_APP = 'blacklisted_app';

// Held reason that all mean the user has to upgrade their plan:
export const PLAN_LIMIT_HELD_REASONS = [
  'task_limit',
  'step_limit',
  PREMIUM_APP,
];

const reducePlanLimitReasons = value =>
  _.reduce(
    PLAN_LIMIT_HELD_REASONS,
    (labels, reason) => {
      labels[reason] = value;
      return labels;
    },
    {}
  );

export const HELD_STATUS_ICONS = {
  too_many: 'throttle',
  stale_auth: 'plug',
  paymentIssue: 'credit-card',
  ...reducePlanLimitReasons('credit-card'),
};

export const ZAP_FILTER_SPECIAL_VALUES = {
  all: 'all',
  private: 'private',
  shared: 'shared',
};

// Reasons why a task is held. More will be coming with each NPZ release....
// DEDUPE_CHANGE: We only show these as held tasks in TH - no blocking held_reason or dashboard notice
export const HeldReasons = {
  STALE_AUTH: 'stale_auth',
  TOO_MANY: 'too_many',
  DEDUPE_CHANGE: 'dedupe_change',
  PLAN_LIMIT: 'plan_limit',
  PAYMENT_ISSUE: 'paymentIssue',
  THROTTLED: 'throttled',
  BLACKLISTED_APP: 'access_blacklisted_app',
};

export const REPLAY_STEP = 'replayStep';

export const SEE_DETAILS_URLS = {
  stale_auth: '/app/connections',
  plan_limit: '/app/settings/billing/plan',
};

export const HELD_STALE_AUTH_ID = 'held_stale_auth_id';

export const HELD_STATUS = 'held';

export const HELD_REASONS_QUERY_PARAM = 'held|throttled';

export const TASK_STATUSES = [
  { value: '0', label: 'Everything' },
  { value: 'success', label: 'Success' },
  { value: 'filtered', label: 'Filtered' },
  { value: 'error|halted', label: 'Stopped / All' },
  { value: 'error', label: 'Stopped / Errored' },
  { value: 'halted', label: 'Stopped / Halted' },
  { value: 'delayed|scheduled', label: 'Waiting / All' },
  { value: 'delayed', label: 'Waiting / Delayed' },
  { value: 'scheduled', label: 'Waiting / Scheduled' },
  { value: 'pending', label: 'Playing' },
  { value: HELD_REASONS_QUERY_PARAM, label: 'Holding' },
];

export const SUB_STATUS_LABELS = {
  success: '',
  error: 'Errored',
  halted: 'Halted',
  filtered: 'Didn’t Pass Filter',
  delayed: 'Delayed',
  scheduled: 'Scheduled',
  throttled: 'Throttled',
};

type HeldReasonMessageMap = { [key: *]: string };

export const HELD_STATUS_LABELS: HeldReasonMessageMap = {
  too_many: 'High Task Use',
  stale_auth: 'Reconnect Needed',
  paymentIssue: 'Payment Issue',
  ...reducePlanLimitReasons('Plan Limit Exceeded'),
};

export const MASS_REPLAY_STATUSES: { [key: string]: (n: number) => string } = {
  // successful replay
  success: n => `${n} ${pluralize('Task', n)} ran successfully!`,

  // did not attempt to replay:
  paused_zap: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because their associated Zaps are turned off. Turn on the Zap to enable replay.`,
  successful_task: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because they already ran successfully.`,
  outstanding_task: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because they’re already scheduled for replay.`,
  diverged_zap: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because the Zap has changed too much.`,
  expired_task: n =>
    `We didn’t retry ${n} ${pluralize('Task', n)} because the Task is too old.`,
  unsuccessful_task: n =>
    `${n} ${pluralize(
      'Task',
      n
    )} could not be replayed because they failed at the trigger step.`,
  deleted_zap: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because their associated Zaps no longer exist.`,
  playing_task: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because playing Tasks cannot be replayed.`,
  stale_auth: n =>
    `We couldn't play ${n} Tasks because a connected account expired.`,
  plan_limit: () =>
    'Could not cannot replay tasks because this account has hit its plan limit.',

  // replayed, but failed:
  error: n => `${n} ${pluralize('Task', n)} were stopped due to a Zap issue.`,
  halted: n => `${n} ${pluralize('Task', n)} were stopped due to a data issue.`,
  filtered: n =>
    `${n} ${pluralize('Task', n)} were stopped because they were filtered.`,
  delayed: n => `${n} ${pluralize('Task', n)} are waiting on a delay step.`,
  scheduled: n =>
    `${n} ${pluralize('Task', n)} are waiting on a schedule step.`,
  throttled: n => `${n} ${pluralize('Task', n)} were throttled.`,
};

export const MASS_REPLAY_ERRORS_FREE_PLAN: {
  [key: string]: (n: number) => string,
} = {
  ...MASS_REPLAY_STATUSES,
  successful_task: n =>
    `We didn’t retry ${n} ${pluralize(
      'Task',
      n
    )} because only held Tasks can be played.`,
};

export const MASS_DELETE_STATUSES: { [key: string]: (n: number) => string } = {
  // successful delete
  deleted: n => `${n} ${pluralize('Task', n)} deleted!`,

  // did not attempt to delete:
  expired_task: n =>
    `We couldn't delete ${n} ${pluralize(
      'Task',
      n
    )} because they were too old.`,
  playing_task: n =>
    `We couldn't delete ${n} ${pluralize(
      'Task',
      n
    )} because playing Tasks cannot be deleted.`,
};

type ReplayStatus = $Keys<typeof MASS_REPLAY_STATUSES>;

export const REPLAY_STATUS_MAP: { [ReplayStatus]: string } = {
  // successful
  success: 'The tasks below ran successfully.',

  // did not attempt to replay:
  paused_zap:
    'The tasks below did not run because their associated Zaps are turned off. Turn on the Zap to enable replay.',
  successful_task:
    'The tasks below did not run because they already ran successfully.',
  outstanding_task:
    'The tasks below did not run because they’re already scheduled for replay.',
  diverged_zap:
    'The tasks below did not run because the Zap has changed too much.',
  expired_task: 'The tasks below did not run because the task is too old.',
  unsuccessful_task:
    'The tasks below did not run because they failed at the trigger step.',
  deleted_zap:
    'The tasks below did not run because their associated Zaps no longer exist.',
  playing_task:
    'The below Tasks were not deleted because playing Tasks cannot be deleted.',
  stale_auth:
    'The tasks below did not run because a connected account expired.',

  // replayed, but failed:
  error: 'The tasks below were stopped due to a Zap issue.',
  halted: 'The tasks below were stopped due to a data issue.',
  filtered: 'The tasks below were stopped because they were filtered.',
  delayed: 'The tasks below are waiting on a delay step.',
  scheduled: 'The tasks below are waiting on a schedule step.',
  throttled: 'The tasks below were throttled.',
};

export const STATUS_LABELS = {
  success: 'Success',
  error: 'Stopped',
  halted: 'Stopped',
  filtered: 'Filtered',
  delayed: 'Waiting',
  scheduled: 'Waiting',
  pending: 'Playing',
  held: 'Holding',
  deleted: 'Deleted',
  throttled: 'Holding',
};

// map of mass action name (ie 'replay', 'delete') and status label
export const RUNNING_STATUS_LABELS = {
  replay: 'Playing',
  delete: 'Deleting',
};

export const SEE_DETAILS_TEXT: HeldReasonMessageMap = {
  stale_auth: 'Reconnect your account to try again.',
  plan_limit: 'Upgrade Now',
};

type FailureLink = { text: string, href: string };

export const MASS_REPLAY_DOWN = 'mass_replay_down';
export const PLAN_LIMIT = 'plan_limit';
export const PAYMENT_ISSUE = 'payment_issue';

export const GLOBAL_FAILURE_LINKS: { [string]: FailureLink } = {
  mass_replay_down: {
    text: 'Zapier Status',
    href: 'https://status.zapier.com',
  },
  plan_limit: {
    text: 'Upgrade Now',
    href: '/app/billing/plans',
  },
  [PAYMENT_ISSUE]: {
    text: 'Update Card',
    href: CREDIT_CARD_URL,
  },
};
